import { MasterDocument } from '@util/firestore/master';
import { ListingDisplay } from '@c/MasterProductsPageV2';
import { extractKeySpecs } from '@util/master/extractKeySpecs';

interface AnswerCardProps {
  master: MasterDocument;
  listings: ListingDisplay[];
}

/**
 * Maps a productType string to a human-readable display label.
 * Used in h1 and sentence 1 of the answer card.
 */
function getProductTypeLabel(productType?: string): string {
  const map: Record<string, string> = {
    'mirrorless_camera': 'mirrorless camera',
    'dslr_camera': 'DSLR camera',
    'cinema_camera': 'cinema camera',
    'medium_format_camera': 'medium format camera',
    'compact_camera': 'compact camera',
    'film_camera': 'film camera',
    'action_camera': 'action camera',
    'broadcast_camera': 'broadcast camera',
    'camcorder': 'camcorder',
    '3d_360_camera': '360 camera',
    'other_camera': 'camera',
    'photo_lens': 'lens',
    'cinema_lens': 'cinema lens',
    'drone': 'drone',
    'microphone': 'microphone',
    'recorder': 'recorder',
    'lighting': 'lighting equipment',
    'tripods': 'tripod',
    'camera_bags': 'camera bag',
    'memory_card': 'memory card',
    'lens_accessory': 'lens accessory',
  };
  return map[productType ?? ''] ?? 'camera gear';
}

/**
 * Truncates a string to a maximum word count, appending "..." if truncated.
 */
function truncateWords(text: string, maxWords: number): string {
  const words = text.trim().split(/\s+/);
  if (words.length <= maxWords) return text.trim();
  return words.slice(0, maxWords).join(' ') + '...';
}

/**
 * Cleans sensor type string for natural prose.
 * "Full Frame (35mm)" → "full-frame", preserves "APS-C", "Micro Four Thirds", etc.
 */
function formatSensorForProse(value: string): string {
  return value
    .replace(/\s*\([^)]*\)/g, '')
    .replace(/\bFull Frame\b/gi, 'full-frame')
    .trim();
}

/**
 * Builds 2–3 sentence natural-language answer card text.
 *
 * Sentence 1 — Product identity with em-dash elaboration (what makes it notable).
 * Sentence 2 — Pricing from active listings; falls back to availability line.
 */
function buildAnswerText(master: MasterDocument, listings: ListingDisplay[]): string {
  const typeLabel = getProductTypeLabel(master.productType);
  const keySpecs = extractKeySpecs(
    master.specifications as Record<string, unknown> | undefined,
    master.productType
  );
  const isLens =
    master.productType === 'photo_lens' || master.productType === 'cinema_lens';
  const features = master.gmFeatures;

  let identitySentence: string;

  if (isLens) {
    const focalSpec = keySpecs.find((s) => s.label === 'Focal Length');
    const apertureSpec = keySpecs.find((s) => s.label === 'Maximum Aperture');
    const mountSpec = keySpecs.find((s) => s.label === 'Lens Mount');

    const specParts: string[] = [];
    if (focalSpec) specParts.push(focalSpec.value);
    if (apertureSpec) specParts.push(apertureSpec.value);
    const specStr = specParts.join(' ');

    if (specStr && mountSpec) {
      identitySentence = `The ${master.brand} ${master.model} is a ${specStr} ${typeLabel} designed for ${mountSpec.value} mount cameras.`;
    } else if (specStr) {
      identitySentence = `The ${master.brand} ${master.model} is a ${specStr} ${typeLabel} from ${master.brand}.`;
    } else {
      identitySentence = `The ${master.brand} ${master.model} is a ${typeLabel} from ${master.brand}.`;
    }
  } else {
    const resSpec = keySpecs.find((s) => s.label === 'Resolution');
    const sensorSpec = keySpecs.find((s) => s.label === 'Sensor');

    let descriptor = '';
    if (resSpec && sensorSpec) {
      descriptor = `${resSpec.value} ${formatSensorForProse(sensorSpec.value)}`;
    } else if (resSpec) {
      descriptor = resSpec.value;
    } else if (sensorSpec) {
      descriptor = formatSensorForProse(sensorSpec.value);
    }

    // Build em-dash elaboration: prefer video spec, then gmFeatures
    const videoSpec = keySpecs.find((s) => s.label === 'Max Video Resolution');
    const stabSpec = keySpecs.find((s) => s.label === 'Image Stabilization');

    let emDash = '';
    if (videoSpec) {
      const extras: string[] = [`${videoSpec.value} video`];
      if (stabSpec && /body|ibis/i.test(stabSpec.value)) {
        extras.push('in-body stabilization');
      }
      emDash = ` — with ${extras.join(' and ')}`;
    } else if (features && features.length >= 2) {
      emDash = ` — known for its ${features[0].title} and ${features[1].title}`;
    } else if (features && features.length === 1) {
      emDash = ` — known for its ${features[0].title}`;
    }

    if (descriptor) {
      identitySentence = `The ${master.brand} ${master.model} is a ${descriptor} ${typeLabel}${emDash}.`;
    } else if (emDash) {
      identitySentence = `The ${master.brand} ${master.model} is a ${typeLabel} from ${master.brand}${emDash}.`;
    } else {
      identitySentence = `The ${master.brand} ${master.model} is a ${typeLabel} from ${master.brand}.`;
    }
  }

  // Price sentence (always last)
  const activePrices = listings
    .filter((l) => !l.out_of_stock && l.is_verified)
    .map((l) => l.price)
    .sort((a, b) => a - b);

  let priceSentence: string;
  if (activePrices.length > 0) {
    priceSentence = `Used copies start from $${activePrices[0].toLocaleString()} on GearFocus, with buyer protection on every purchase.`;
  } else {
    priceSentence = 'Check GearFocus for current availability, with buyer protection on every purchase.';
  }

  return `${identitySentence} ${priceSentence}`;
}

/**
 * AnswerCard — SSR-generated product summary section.
 *
 * Designed to be the FIRST <p> tag after <h1> so AI search engines
 * (ChatGPT, Perplexity, Google AI Overviews) extract it as the canonical
 * product summary. Must be rendered server-side with no "use client" directive.
 */
export default function AnswerCard({ master, listings }: AnswerCardProps) {
  const answerText = buildAnswerText(master, listings);

  return (
    <section
      aria-label="Product Summary"
      data-geo="answer-card"
      className="sr-only"
    >
      <h1 className="text-[2.4rem] sm:text-[2.8rem] font-semibold text-brand-dark-gray">
        {master.title}
      </h1>
      <p className="answer-card mt-[1.2rem] text-[1.6rem] leading-[2.4rem] text-brand-dark-gray">
        {answerText}
      </p>
    </section>
  );
}
